import apiClient from "@/services/api/apiClient"
import type { UserConfigPayload } from "@/types/userConfig"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

export const USER_CONFIG_QUERY_KEY = ["userConfig"] as const

/**
 * Structure
 * 
{
    "configuration": {
        "knowledgebase": {
            "file_table": {
                "active_category_column_ids": [],
                "is_expiry_date_column_active": false,
                "column_widths_by_id": {}
            }
        }
    }
}
 * 
 * 
 */
const userConfigApi = {
  getUserConfig: async (): Promise<UserConfigPayload> => {
    const response = await apiClient.get<UserConfigPayload>("/user-config/")
    return response.data
  },

  updateUserConfig: async (
    payload: UserConfigPayload,
  ): Promise<UserConfigPayload> => {
    const response = await apiClient.put<UserConfigPayload>(
      "/user-config/",
      payload,
    )
    return response.data
  },
}

export const useGetUserConfig = (enabled = true) => {
  return useQuery({
    enabled,
    queryKey: USER_CONFIG_QUERY_KEY,
    queryFn: userConfigApi.getUserConfig,
  })
}

export const useUpdateUserConfig = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: userConfigApi.updateUserConfig,
    onSuccess: (data) => {
      queryClient.setQueryData(USER_CONFIG_QUERY_KEY, data)
    },
  })
}

export const useTaggingFlowVideoOnboarding = () => {
  const { data: userConfig, isPending } = useGetUserConfig()
  const { mutateAsync: updateUserConfig } = useUpdateUserConfig()
  const [dismissedLocally, setDismissedLocally] = useState(false)

  const hasWatched =
    isPending ||
    dismissedLocally ||
    (userConfig?.configuration?.onboarding?.taggingFlowVideo ?? false)

  const markAsWatched = async () => {
    // Close the dialog immediately — best-effort persist
    setDismissedLocally(true)
    if (!userConfig) return
    try {
      await updateUserConfig({
        configuration: {
          ...userConfig.configuration,
          onboarding: {
            ...userConfig.configuration.onboarding,
            taggingFlowVideo: true,
          },
        },
      })
    } catch {
      // Persist failed; dialog stays closed for this session but the
      // indicator may reappear on the next page load.
    }
  }

  return { hasWatched, markAsWatched }
}
